'use client';

import { useCallback, useRef, useState, memo } from 'react';
import { useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import useShoppingCart from '@/hooks/useShoppingCart';
import type { ShoppingCartItem } from '@/models/ShoppingCartItem';
import Button from '@/components/shared/button/Button';
import Icon from '@/components/shared/Icons';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import type { RequestQuoteButtonStrings } from '@/utils/venueCardHelper';
import type { ShoppingCartFact } from '@/analytics/models/cventAnalytics/ShoppingCartFact';
import { getShoppingCartFact } from '@/analytics/helpers/cventAnalytics/getShoppingCartFact';
import { queryStringConstants } from '@/constants/queryConstants';
import useAnalytics from '@/hooks/useAnalytics';
import useMap from '@/hooks/useMap';
import reportToCventAdTrack from '@/analytics/cventAdTrackReporter';
import useLogger from '@/hooks/useLogger';

const Popper = dynamic(() => import('@/components/shared/popper/Popper'), { ssr: false });

const VenueCardDisplayType = {
  CSN_AVAILABLE: 'CSN_AVAILABLE',
  CSN_NON_AVAILABLE: 'CSN_NON_AVAILABLE',
  WHITE_LABEL: 'WHITE_LABEL',
  BUSINESS_TRANSIENT: 'BUSINESS_TRANSIENT'
} as const;

export type VenueCardDisplayType = (typeof VenueCardDisplayType)[keyof typeof VenueCardDisplayType];

export type RequestQuoteProps = {
  item: ShoppingCartItem;
  csnSearchAppServiceBaseUrl?: string;
  adSessionId?: string;
  isBlock?: boolean;
  strings?: RequestQuoteButtonStrings;
  isRFPFullAccess?: boolean;
  displayTypes?: VenueCardDisplayType[];
  isLoggedIn: boolean;
  iconOnly?: boolean;
  testID?: string;
  className?: string;
  activeFilters?: string;
  pageType?: string;
  isVpp?: boolean;
  environment?: string;
  isNearByVenue?: boolean;
};

function RequestQuoteButton({
  item,
  csnSearchAppServiceBaseUrl,
  adSessionId,
  strings,
  isBlock,
  isRFPFullAccess,
  displayTypes,
  isLoggedIn,
  activeFilters,
  testID = 'venue',
  iconOnly = false,
  className,
  pageType,
  isVpp,
  environment,
  isNearByVenue
}: RequestQuoteProps): JSX.Element {
  const cart = useShoppingCart();
  const [isOpen, setIsOpen] = useState(false);
  const buttonTriggerRef = useRef<HTMLButtonElement>(null);
  const { isMobile } = useBreakpoints();
  const searchParams = useSearchParams();
  const analytics = useAnalytics();
  const { mapView } = useMap();
  const logger = useLogger('RequestQuoteButton');
  const rfpId = searchParams?.get('rfpId') ?? 'EXTERNAL_SHOPPING_CART';
  const type = cart?.contains(item.venueId) || !cart?.isRfpSentToVenue(item.venueId) ? 'delete' : 'add';
  const isDiamondPlusAd = item.featuredLevel === 'DIAMOND_PLUS' || item.featuredLevel === 'DIAMOND_PLUS_BRAND';
  let isDisabled = false;
  let buttonText = type === 'delete' ? strings?.delete : strings?.add;
  let tooltipText;

  const onMouseEvent = useCallback(async (e, shouldOpen) => {
    e.preventDefault();
    setIsOpen(shouldOpen);
  }, []);

  const triggerShoppingCartFact = useCallback(() => {
    const shoppingCartFact: ShoppingCartFact = getShoppingCartFact(
      type,
      item,
      cart,
      searchParams?.get(queryStringConstants.PAGE_NUMBER),
      searchParams?.get(queryStringConstants.SORT),
      mapView,
      activeFilters,
      pageType
    );
    analytics.reportFact<ShoppingCartFact>(shoppingCartFact);

    if (isDiamondPlusAd && type === 'add' && csnSearchAppServiceBaseUrl && adSessionId && environment) {
      const adData = [
        {
          supplierId: item.venueId,
          adCode: item.adId,
          competitiveSetFlag: false,
          needDatesFlag: false
        }
      ];
      reportToCventAdTrack(
        csnSearchAppServiceBaseUrl,
        adData,
        adSessionId,
        rfpId === 'EXTERNAL_SHOPPING_CART' ? undefined : rfpId,
        'AD_CLICKED',
        logger,
        environment
      );
    }
  }, [
    type,
    item,
    cart,
    searchParams,
    mapView,
    activeFilters,
    pageType,
    analytics,
    isDiamondPlusAd,
    csnSearchAppServiceBaseUrl,
    adSessionId,
    environment,
    rfpId,
    logger
  ]);

  const onClick = useCallback(
    async (e, isButtonDisabled) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.pointerType === 'touch' && isButtonDisabled) {
        setIsOpen(!isOpen);
      }

      cart.updateCart({ type, item, rfpId });
      triggerShoppingCartFact();
    },
    [cart, item, type, rfpId, isOpen, triggerShoppingCartFact]
  );

  const displayedIcon = useCallback(() => {
    if (!isNearByVenue) {
      if (type === 'delete') {
        return <Icon id="check" className={`h-4 w-4 ${isDisabled ? 'fill-brand-60' : 'fill-background'}`} />;
      }
      if (!isVpp && type === 'add') {
        return (
          <Icon id="plus" className={`h-4 w-4 fill-brand-60 ${!isDisabled ? 'group-hover:fill-background' : ''}`} />
        );
      }
    }
    return '';
  }, [isDisabled, isVpp, type, isNearByVenue]);

  if (isNearByVenue) {
    buttonText = type === 'delete' ? strings?.nearByVenueRemoveFromList : strings?.nearByVenueAddToList;
  }

  // Disable button if venue is already added to rfp or rfp is sent with informational tooltip
  if (!cart?.isRfpSentToVenue(item.venueId)) {
    isDisabled = true;
    buttonText = strings?.addedToRfp;
  }

  // Disable button if cart is already full for guest/logged in user with informational tooltip
  if (cart?.isFull() && !cart?.contains(item.venueId)) {
    isDisabled = true;
    tooltipText = strings?.shoppingCartFull;
  }

  // Disable button if user is logged in but do not have access to manage rfp
  if (isLoggedIn && !isRFPFullAccess) {
    isDisabled = true;
    tooltipText = strings?.noRfpAccess;
  }

  // Disable button if venue is not interested in receiving rfp
  if (!!displayTypes && displayTypes.includes(VenueCardDisplayType.CSN_NON_AVAILABLE)) {
    isDisabled = true;
    tooltipText = strings?.supplierNotInterested;
  }

  return (
    <div>
      <Button
        testID={`${testID}-select-venue`}
        text={iconOnly ? undefined : buttonText}
        ref={buttonTriggerRef}
        disabled={isDisabled}
        aria-label={buttonText}
        icon={displayedIcon}
        onClick={e => onClick(e, isDisabled)}
        active={type === 'delete'}
        className={`group ${className}`}
        isBlock={isBlock}
        onMouseOver={e => onMouseEvent(e, true)}
        onMouseOut={e => onMouseEvent(e, false)}
        onTouchMove={e => onMouseEvent(e, false)}
        appearance={isVpp ? 'filled' : 'lined'}
      />
      {tooltipText && (
        <Popper
          testID={`${testID}-select-venue-tooltip`}
          triggerRef={buttonTriggerRef}
          isOpen={isOpen}
          arrowSize="small"
          placement={isMobile ? 'top' : 'bottom'}
          className={`${isVpp ? 'w-9/12' : 'w-40'} rounded-lg bg-neutral-70 before:!border-neutral-70`}
          // setting arrowStyles to match the tooltip arrow color with the tooltip background color
          arrowStyles={{
            borderColor: '#4e545b'
          }}
          // setting popperjsModifiers to add offset to the tooltip for VPP
          popperjsModifiers={
            isVpp
              ? [
                  {
                    name: 'offset',
                    options: {
                      offset: [50, 10]
                    }
                  }
                ]
              : undefined
          }
        >
          <div className="overflow-y-hidden rounded-lg p-4 text-left text-b-xs/tight text-background shadow-4">
            {tooltipText}
          </div>
        </Popper>
      )}
    </div>
  );
}

export default memo(RequestQuoteButton);
